import { useLocation } from 'react-router';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useContainerQuery } from './ContainerBreakpoint';
import { BreadcrumbTerminalSlotHost } from 'src/UI/page-nav/BreadcrumbTerminalSlot';
import { BreadcrumbItem, BreadcrumbTrail } from './BreadcrumbTrail';

export function AppBreadcrumbs() {
  const location = useLocation();
  const { pathname: path, search } = location;
  const { clientId } = useCurrentClientIfAvailable();
  const { flagEnabledResponsiveMenus, flagEnabledDataUploadReviewNavigation } = useFlags();
  const cq = useContainerQuery();
  const isCompact = cq({ base: true, xs: false });

  const onClientPage = clientId != null && path.startsWith(`/client/${clientId}`);
  // The cashflow model page is a full-bleed takeover with its own breadcrumb;
  // a second trail above it would sit on the page's own background. It lives
  // at /client/:id/cashflows/model/:scenarioId and the same under /family.
  const [, owner, , section, page, scenarioId] = path.split('/');
  const onCashflowModelPage =
    (owner === 'client' || owner === 'family') &&
    section === 'cashflows' &&
    page === 'model' &&
    Boolean(scenarioId);
  if (onCashflowModelPage) {
    return null;
  }
  // The data upload review draws its own trail.
  const onDataUploadReview = owner === 'client' && section === 'detected-entities';
  if (flagEnabledDataUploadReviewNavigation && onDataUploadReview) {
    return null;
  }
  if (onClientPage) {
    // Hide the breadcrumb trail on the reworked client pages. Only when the tabs collapse on narrow
    // viewports do we keep the terminal slot mounted, so the collapsed mobile tabs (which portal
    // into it via integrateBreadcrumb) still render. On wider viewports render nothing — an empty
    // host would add a stray line of vertical space above the content.
    if (flagEnabledResponsiveMenus && isCompact) {
      return <BreadcrumbTerminalSlotHost />;
    }
    return null;
  }

  const items = buildBreadcrumbItems(search);

  if (items.length === 0) {
    return null;
  }

  return (
    <BreadcrumbTrail
      items={items}
      mx={cq({ base: 'xs', xl: 'sm' })}
      mb={cq({ base: 'xs', xl: 'md' })}
      mt={cq<string | number>({ base: 0, md: 'xs', lg: 'sm', xl: 'md' })}
    >
      <BreadcrumbTerminalSlotHost />
    </BreadcrumbTrail>
  );
}

function buildBreadcrumbItems(search: string) {
  const parts: BreadcrumbItem[] = [];

  if (search.includes('fileName')) {
    const fileName = new URLSearchParams(search).get('fileName');
    parts.push({ label: fileName, href: window.location.href });
  }

  return parts;
}
